'use strict'

/**
 * Постраничная навигация.
 *
 * На мобильных экранах кнопки навигации не должны «разъезжаться» и выходить
 * за пределы контейнера: если кнопки не помещаются в одну строку, часть из них
 * скрывается. Приоритет кнопок (скрываются в последнюю очередь):
 *   1. текущая страница;
 *   2. стрелки «<» и «>»;
 *   3. многоточия «...»;
 *   4. первая и последняя страницы;
 *   5. страницы рядом с текущей (чем дальше, тем ниже приоритет).
 *
 * Текущая страница и стрелки видны всегда. Многоточия видны, пока для них
 * хватает места: в крайнем случае (очень узкий экран) скрываются и они.
 *
 * Серверная часть (шаблоны system.pagenavigation) помечает классом "mob" кнопки,
 * которые могут отображаться на мобильных. Лишние кнопки скрывает этот скрипт —
 * по фактической ширине экрана.
 *
 * Скрипт собирается отдельным бандлом (pagination.bundle.js) и подключается
 * только на страницах с постраничной навигацией.
 */
class Pagination {
    /** Максимальная ширина экрана, на которой включается мобильный режим (см. $mobileBreakpoint). */
    static MOBILE_BREAKPOINT = 768

    /** Класс, которым помечается скрытая кнопка. */
    static HIDDEN_CLASS = 'pagination-item_is-hidden'

    /** Приоритеты кнопок: меньшее значение — важнее. */
    static PRIORITY = {
        CURRENT: 0,
        ARROW: 1,
        DOTS: 2,
        BOUNDARY: 3,
        NEIGHBOUR: 4,
        OTHER: 100,
    }

    static init() {
        if (Pagination.initialised) {
            return
        }
        Pagination.initialised = true

        Pagination.mobileQuery = window.matchMedia(`(max-width: ${Pagination.MOBILE_BREAKPOINT}px)`)
        Pagination.lastWidths = new WeakMap()
        Pagination.force = false

        // Ждём следующий кадр: во время resize браузер может ещё не успеть
        // пересчитать стили (ширины кнопок, медиазапросы).
        const update = Pagination.debounce(() => {
            requestAnimationFrame(() => {
                const force = Pagination.force
                Pagination.force = false
                Pagination.updateAll(force)
            })
        }, 100)

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => Pagination.updateAll(true))
        } else {
            Pagination.updateAll(true)
        }

        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', () => {
            Pagination.force = true
            update()
        })

        // В каталоге постраничная навигация подгружается ajax-ом,
        // поэтому пересчитываем кнопки при изменениях DOM.
        const observer = new MutationObserver((mutations) => {
            for (let i = 0; i < mutations.length; i++) {
                const mutation = mutations[i]
                if (Pagination.containsPagination(mutation.target)) {
                    Pagination.force = true
                    update()
                    return
                }
                const added = mutation.addedNodes
                for (let j = 0; j < added.length; j++) {
                    if (Pagination.containsPagination(added[j])) {
                        Pagination.force = true
                        update()
                        return
                    }
                }
            }
        })
        observer.observe(document.documentElement, {childList: true, subtree: true})
    }

    /**
     * Пересчитывает видимые кнопки всех навигаций на странице.
     * @param {boolean} force Пересчитать, даже если ширина контейнеров не менялась.
     */
    static updateAll(force) {
        const paginations = document.querySelectorAll('.pagination')
        if (paginations.length === 0) {
            return
        }

        if (!Pagination.mobileQuery.matches) {
            // На десктопе видимостью кнопок управляет только css — снимаем
            // классы, выставленные для мобильного режима.
            for (let i = 0; i < paginations.length; i++) {
                Pagination.showAll(paginations[i])
            }
            // Кэш ширин сбрасываем, чтобы при возврате на мобильную ширину
            // навигация пересчиталась заново.
            Pagination.lastWidths = new WeakMap()
            return
        }

        // Дешёвое чтение: если ширина контейнеров не менялась с прошлого
        // расчёта, пересчитывать нечего (частые resize из-за адресной строки).
        let changed = force === true
        const widths = new Array(paginations.length)
        for (let i = 0; i < paginations.length; i++) {
            const pagination = paginations[i]
            const width = pagination.clientWidth
            widths[i] = width
            if (!changed && Pagination.lastWidths.get(pagination) !== width) {
                changed = true
            }
        }
        if (!changed) {
            return
        }

        const gap = Pagination.getGap(paginations[0])

        // Фаза чтения: измеряем кнопки и рассчитываем видимость, ничего не
        // записывая в DOM — так браузер делает минимум пересчётов макета.
        const plans = []
        for (let i = 0; i < paginations.length; i++) {
            const pagination = paginations[i]
            Pagination.lastWidths.set(pagination, widths[i])

            const pool = Pagination.getMobileItems(pagination)
            if (pool.length === 0) {
                continue
            }

            let buttonWidth = Pagination.measureButtonWidth(pool)
            if (buttonWidth === 0) {
                // Все кнопки были скрыты при прошлом расчёте — показываем
                // их, чтобы измерить ширину (случай маловероятный).
                Pagination.showAll(pagination)
                buttonWidth = Pagination.measureButtonWidth(pool)
            }
            if (buttonWidth === 0) {
                continue
            }

            const limit = Math.max(Math.floor((widths[i] + gap) / (buttonWidth + gap)), 1)
            plans.push({
                pool: pool,
                keep: pool.length <= limit ? null : Pagination.getKeepMask(pool, limit),
            })
        }

        // Фаза записи: одним проходом показываем/скрываем только те кнопки,
        // видимость которых изменилась.
        for (let i = 0; i < plans.length; i++) {
            const pool = plans[i].pool
            const keep = plans[i].keep
            for (let j = 0; j < pool.length; j++) {
                const hidden = keep !== null && keep[j] === 0
                if (hidden !== pool[j].classList.contains(Pagination.HIDDEN_CLASS)) {
                    pool[j].classList.toggle(Pagination.HIDDEN_CLASS, hidden)
                }
            }
        }
    }

    /**
     * Кнопки, которые могут отображаться на мобильных (класс "mob").
     * @param {Element} pagination
     * @returns {Element[]}
     */
    static getMobileItems(pagination) {
        const pool = []
        for (let item = pagination.firstElementChild; item !== null; item = item.nextElementSibling) {
            if (item.classList.contains('pagination-item') && item.classList.contains('mob')) {
                pool.push(item)
            }
        }
        return pool
    }

    /**
     * Снимает класс скрытия со всех кнопок навигации.
     * @param {Element} pagination
     */
    static showAll(pagination) {
        const hidden = pagination.querySelectorAll('.' + Pagination.HIDDEN_CLASS)
        for (let i = 0; i < hidden.length; i++) {
            hidden[i].classList.remove(Pagination.HIDDEN_CLASS)
        }
    }

    /**
     * Ширина ссылки-кнопки. Ориентируемся на ширину ссылки: у li может быть
     * margin (стрелки на десктопе), а при не помещающихся кнопках li ещё и
     * сжимается.
     * @param {Element[]} pool
     * @returns {number}
     */
    static measureButtonWidth(pool) {
        let width = 0
        for (let i = 0; i < pool.length; i++) {
            const link = pool[i].querySelector('.pagination-item__link_only-mobile')
                || pool[i].querySelector('.pagination-item__link')
            if (link === null) {
                continue
            }
            const linkWidth = link.getBoundingClientRect().width
            if (linkWidth > width) {
                width = linkWidth
            }
        }
        return Math.ceil(width)
    }

    /**
     * Маска видимых кнопок пула.
     * @param {Element[]} pool
     * @param {number} limit Сколько кнопок помещается в строку.
     * @returns {Uint8Array}
     */
    static getKeepMask(pool, limit) {
        const count = pool.length
        const keep = new Uint8Array(count)
        const priorities = Pagination.getPriorities(pool)

        // Обязательный минимум — текущая страница и стрелки; многоточия
        // попадают в набор, только если для них хватает места.
        let mandatory = 0
        for (let i = 0; i < count; i++) {
            if (priorities[i] < Pagination.PRIORITY.DOTS) {
                mandatory++
            }
        }

        const keepCount = Math.min(Math.max(limit, mandatory), count)
        if (keepCount === count) {
            keep.fill(1)
            return keep
        }

        const order = new Array(count)
        for (let i = 0; i < count; i++) {
            order[i] = i
        }
        order.sort((a, b) => priorities[a] - priorities[b] || a - b)

        for (let i = 0; i < keepCount; i++) {
            keep[order[i]] = 1
        }
        return keep
    }

    /**
     * Приоритеты кнопок: меньшее значение — важнее.
     * @param {Element[]} pool
     * @returns {number[]}
     */
    static getPriorities(pool) {
        const kinds = pool.map((item) => Pagination.getKind(item))

        const currentIndex = kinds.findIndex((kind) => kind.type === 'current')
        const currentPage = currentIndex === -1 ? null : kinds[currentIndex].page

        // Границы пула — первая и последняя страницы с номером (текущая тоже
        // может быть крайней, например «1» в начале списка).
        const pageIndexes = kinds
            .map((kind, index) => (kind.page !== null ? index : -1))
            .filter((index) => index !== -1)
        const firstPageIndex = pageIndexes.length ? pageIndexes[0] : -1
        const lastPageIndex = pageIndexes.length ? pageIndexes[pageIndexes.length - 1] : -1

        return kinds.map((kind, index) => {
            switch (kind.type) {
                case 'current':
                    return Pagination.PRIORITY.CURRENT
                case 'arrow':
                    return Pagination.PRIORITY.ARROW
                case 'dots':
                    return Pagination.PRIORITY.DOTS
                case 'page':
                    if (index === firstPageIndex || index === lastPageIndex) {
                        return Pagination.PRIORITY.BOUNDARY
                    }
                    if (currentPage === null) {
                        return Pagination.PRIORITY.NEIGHBOUR
                    }
                    return Pagination.PRIORITY.NEIGHBOUR + Math.abs(kind.page - currentPage) - 1
                default:
                    return Pagination.PRIORITY.OTHER
            }
        })
    }

    /**
     * Определяет тип кнопки по её разметке. На мобильных может быть две ссылки
     * (_only-mobile и _only-desktop) — тип определяется по видимой.
     * @param {Element} item
     * @returns {{type: string, page: (number|null)}}
     */
    static getKind(item) {
        const link = item.querySelector('.pagination-item__link_only-mobile')
            || item.querySelector('.pagination-item__link')
        if (link === null) {
            return {type: 'other', page: null}
        }

        if (link.classList.contains('first') || link.classList.contains('last')) {
            return {type: 'arrow', page: null}
        }

        if (item.querySelector('.pagination-item__link.active') !== null) {
            return {type: 'current', page: Pagination.getPage(link)}
        }

        const text = link.textContent.trim()
        if (text === '...' || text === '…') {
            return {type: 'dots', page: null}
        }

        const page = Pagination.getPage(link)
        return page === null ? {type: 'other', page: null} : {type: 'page', page}
    }

    /**
     * Номер страницы из текста кнопки.
     * @param {Element} link
     * @returns {number|null}
     */
    static getPage(link) {
        const page = parseInt(link.textContent, 10)
        return Number.isNaN(page) ? null : page
    }

    /**
     * Расстояние между кнопками (css gap).
     * @param {Element} pagination
     * @returns {number}
     */
    static getGap(pagination) {
        const styles = window.getComputedStyle(pagination)
        return parseFloat(styles.columnGap || styles.gap) || 0
    }

    /**
     * Содержит ли узел (или его потомки) постраничную навигацию.
     * @param {Node} node
     * @returns {boolean}
     */
    static containsPagination(node) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return false
        }
        return node.classList.contains('pagination') || node.querySelector('.pagination') !== null
    }

    static debounce(callback, delay) {
        let timeout = null
        return () => {
            clearTimeout(timeout)
            timeout = setTimeout(callback, delay)
        }
    }
}

window.TimeCity = window.TimeCity || {};
if (!window.TimeCity.Pagination) {
    window.TimeCity.Pagination = Pagination;
    Pagination.init();
}
